import { ReactElement, useEffect } from 'react';
import { Grid, SelectChangeEvent, useEventCallback } from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import { useFormikContext } from 'formik';
import { DatePicker } from '@mui/lab';
import PmiTextField from 'shell/components/shared/text-field';
import ListRegister from 'civil-status/components/shared/list-register';
import { ResearchNotifications } from 'notifications-from-civil-status/view-models/notifications-list';
import ListAnscValoriDominio from 'civil-status/components/shared/list-ansc-valori-dominio';
import { DomainType } from 'civil-status/constants/domain';
import { CodeScope } from 'civil-status/constants/code-scope';
import OperatorList from 'civil-status/components/shared/operator-list';
import ListMunicipalityFormation, {
  IdOtherMunicipalityFormation,
} from 'civil-status/components/research-notifications-side/filter/filter-notifications/list-municipality-formation';
import { MUNICIPALITY_MILANO } from 'civil-status/constants/municipality-milano';
import { IdAmbito } from 'shell/constants/id-ambito';
import ListAnsc from 'civil-status/components/shared/list-ansc';
import { ItemVM } from 'shell/view-models/item';

interface FormFilterProps {
  idCat?: string;
  withOperator?: boolean;
  scope?: string;
  isFromCivilStatus?: boolean;
  isNotCivilStatus?: boolean;
}

const FormFilter = ({
  idCat,
  withOperator,
  scope,
  isFromCivilStatus,
  isNotCivilStatus,
}: FormFilterProps): ReactElement => {
  const formContext = useFormikContext<ResearchNotifications>();

  const onChangeOperator = useEventCallback((event: SelectChangeEvent<string>) => {
    formContext.setFieldValue('codOperator', event.target.value);
  });

  const onChangeRegister = useEventCallback((event: SelectChangeEvent<string>) => {
    formContext.setFieldValue('typeEvent', event.target.value);
  });

  const onChangeScope = useEventCallback((newValue: string) => {
    if (isNotCivilStatus) {
      const isNotStampa = newValue !== IdAmbito.Stampa;
      formContext.setValues({
        ...(formContext.values || {}),
        scope: newValue,
        municipalityFormation: isNotStampa
          ? IdOtherMunicipalityFormation
          : formContext.values.municipalityFormation || '',
        disabledMunicipalityFormation: isNotStampa,
      });

      return;
    }
    formContext.setFieldValue('scope', newValue);
  });

  const onChangeMunicipality = useEventCallback((event: SelectChangeEvent<string>) => {
    formContext.setFieldValue('municipalityFormation', event.target.value);
  });

  useEffect(() => {
    if (isFromCivilStatus) {
      formContext.setValues({
        ...(formContext.values || {}),
        municipalityFormation: MUNICIPALITY_MILANO.istatCode,
        disabledMunicipalityFormation: true,
      });
    }
  }, [isFromCivilStatus]);

  useEffect(() => {
    if (idCat) {
      formContext.setFieldValue('typeEvent', idCat);
    }
  }, [idCat]);

  // useEffect(() => {
  //   const currentCodeOperator = userProfile.datiLogin?.key?.userName || '';

  //   if (withOperator && currentCodeOperator) {
  //     formContext.setFieldValue('codOperator', currentCodeOperator);
  //   }
  // }, [withOperator, userProfile]);

  const onChangeCode = useEventCallback((item?: ItemVM) => {
    formContext.setFieldValue('code', item?.id);
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <ListRegister
          value={formContext.values.typeEvent || ''}
          error={Boolean(formContext.errors.typeEvent) && formContext.touched.typeEvent}
          onChange={onChangeRegister}
          disabled={Boolean(idCat)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <ListAnscValoriDominio
          label="Ambito"
          value={formContext.values.scope || ''}
          onChange={onChangeScope}
          error={formContext.touched.scope && Boolean(formContext.errors.scope)}
          idDominio={DomainType.RegistryOffice}
          idValoreAnsc={CodeScope.ANAGRAFE}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <ListMunicipalityFormation
          value={formContext.values.municipalityFormation || ''}
          error={
            Boolean(formContext.errors.municipalityFormation) &&
            formContext.touched.municipalityFormation
          }
          onChange={onChangeMunicipality}
          isWithIstat
          disabled={formContext.values.disabledMunicipalityFormation}
        />
      </Grid>
      <Grid container spacing={2} item>
        <Grid item xs={12} md={3}>
          <DatePicker
            label="Data inizio"
            aria-label="Data inizio"
            value={formContext.values.dateStart || null}
            onChange={(value) => {
              if (formContext.values.dateEnd) {
                formContext.setFieldValue('dateStart', value);
                return;
              }

              formContext.setValues({
                ...formContext.values,
                dateStart: value,
                dateEnd: value,
              });
            }}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="dateStart"
                fullWidth
                error={formContext.touched?.dateStart && Boolean(formContext.errors?.dateStart)}
                helperText={formContext.errors?.dateStart}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <DatePicker
            label="Data fine"
            aria-label="Data fine"
            value={formContext.values.dateEnd || null}
            onChange={(value) => formContext.setFieldValue('dateEnd', value)}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="dateEnd"
                fullWidth
                error={formContext.touched?.dateEnd && Boolean(formContext.errors?.dateEnd)}
                helperText={formContext.errors?.dateEnd}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} md={3}>
        <ListAnsc
          label="Codice comunicazione"
          value={formContext.values.code || ''}
          onChange={onChangeCode}
          error={formContext.touched.code && Boolean(formContext.errors.code)}
          idDominio={DomainType.Communication?.toString()}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <ListAnscValoriDominio
          label="Stato aggiornamento"
          value={formContext.values.state || ''}
          onChange={(el) => formContext.setFieldValue('state', el)}
          error={formContext.touched.state && Boolean(formContext.errors.state)}
          idDominio={DomainType.Archive}
          idValoreAnsc={CodeScope.ANAGRAFE}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Intestatari"
          aria-label="Intestatari"
          id="owners"
          fullWidth
          onChange={(event) => {
            formContext.setFieldValue('owners', event.target.value);
          }}
          value={formContext.values?.owners || ''}
          helperText={formContext.errors?.owners}
          error={formContext.touched?.owners && Boolean(formContext.errors?.owners)}
        />
      </Grid>
      {withOperator ? (
        <Grid item xs={12} md={3}>
          <OperatorList
            onChange={onChangeOperator}
            value={formContext.values.codOperator || ''}
            // codiceStatoEvento={formContext.values.stateAct || undefined}
            // disabled={!formContext.values.stateAct}
          />
        </Grid>
      ) : undefined}

      <Grid item container spacing={2} xs={12} justifyContent="space-between" display="flex">
        <Grid item container spacing={2} xs={12} justifyContent="flex-end" display="flex">
          <Grid item xs={12} md={2}>
            <PmiButton
              variant="contained"
              color="secondary"
              fullWidth
              onClick={() =>
                formContext.setValues({
                  code: '',
                  dateEnd: null,
                  dateStart: null,
                  scope: scope || '',
                  state: '',
                  typeEvent: idCat || undefined,
                })
              }
            >
              Pulisci
            </PmiButton>
          </Grid>
          <Grid item xs={12} md={2}>
            <PmiButton variant="contained" color="secondary" fullWidth type="submit">
              Cerca
            </PmiButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

FormFilter.displayName =
  'Research notifications - Filters component - AccordionFilterNotifications - Form Filter component';

export default FormFilter;
